/* eslint-disable no-bitwise */

import { concatByteArrays } from '../util/byteArray';
import { BitWriter } from './bitWriter';
import { get_crc_16bits, get_crc_8bits } from './crc';
import { invariant } from '../util/invariant';
import { computeLinearPredictiveCodingCoefficients } from '../aac/linearPredictiveCoding';
import { exceptionToString } from '../util/exception';

// Maximum number of RICE partitions we can use
const MAX_PARTITIONS = 8;

// We hardcode this value to be the same length as an AAC frame
// so that we can use specific compression parameters for this
// frame size
export const SAMPLES_PER_CHANNEL_PER_FRAME = 1024;

/**
 * This is the size of an uncompressed stereo frame, i.e. where
 * all the data is verbatim. We use it as a baseline to determine
 * whether an encoded frame is actually saving space or not.
 */
const MAX_FRAME_SIZE = 4248;

/**
 * The maximum LPC order we use to compress
 */
const MAX_LPC_ORDER = 8;


/**
 * The buffers needed by Flac compression
 */
export interface FlacCompressionBuffers {
  windowedSamples: Float64Array;
  autocorrelation: Float64Array;
  lpc: Float64Array;
  ref: Float64Array;
  coeffs: Int32Array;
  residual: Int32Array;
}


/**
 * This a wrapper to allow creating the buffers when needed and reusing them.
 */
export interface FlacCompressionBufferWrapper {
  buffers?: FlacCompressionBuffers;
}


export function makeFlacCompressionBuffers(): FlacCompressionBuffers {
  return {
    windowedSamples: new Float64Array(MAX_FRAME_SIZE + 2),
    autocorrelation: new Float64Array(1 + MAX_LPC_ORDER),
    lpc: new Float64Array(1024),
    ref: new Float64Array(MAX_LPC_ORDER),
    coeffs: new Int32Array(MAX_LPC_ORDER),
    residual: new Int32Array(MAX_FRAME_SIZE),
  };
}


/**
 * Given some raw PCM samples, returns a buffer containing a valid .flac file.
 *
 * FLAC format: https://xiph.org/flac/format.html
 */
export function encodeToFlac(
  pcm_s16le_bytes: Uint8Array,
  nChannels: number,
  sampleRateInHz: number,
  compressed: boolean,
): Uint8Array {
  const buffers: Uint8Array[] = [];
  const totalSamples = pcm_s16le_bytes.length / (nChannels * 2);
  buffers.push(writeHeader(nChannels, sampleRateInHz, totalSamples));
  const bufferWrapper: FlacCompressionBufferWrapper = {};

  const frameSize = nChannels * SAMPLES_PER_CHANNEL_PER_FRAME * 2;
  for (let nFrame = 0; nFrame * frameSize < pcm_s16le_bytes.length; nFrame++) {
    const frameBytes = pcm_s16le_bytes.slice(nFrame * frameSize, (nFrame + 1) * frameSize);
    buffers.push(writeFrame(bufferWrapper, nFrame, frameBytes, nChannels, sampleRateInHz, compressed));
  }

  return concatByteArrays(buffers);
}


function writeHeader(nChannels: number, sampleRateInHz: number, totalSamples: number): Uint8Array {
  const bitWriter = new BitWriter();
  // "fLaC", the FLAC stream marker in ASCII
  bitWriter.writeBits(0x664C6143, 32);

  // METADATA_BLOCK_HEADER for block STREAMINFO

  // Last-metadata-block flag: '1' if this block is the last metadata block before the audio blocks
  bitWriter.writeBits(1, 1);

  // BLOCK_TYPE: STREAMINFO
  bitWriter.writeBits(0, 7);

  // Length (in bytes) of metadata to follow (does not include the size of the METADATA_BLOCK_HEADER)
  const metadataLengthInBytes = (16 + 16 + 24 + 24 + 20 + 3 + 5 + 36 + 128) / 8;
  bitWriter.writeBits(metadataLengthInBytes, 24);

  writeStreamInfoMetaDataBlockInternal(bitWriter, nChannels, sampleRateInHz, totalSamples);

  return bitWriter.getBuffer();
}


export function writeStreamInfoMetaDataBlock(nChannels: number, sampleRateInHz: number, totalSamples: number) {
  const bitWriter = new BitWriter();

  writeStreamInfoMetaDataBlockInternal(bitWriter, nChannels, sampleRateInHz, totalSamples);

  return bitWriter.getBuffer();
}


function writeStreamInfoMetaDataBlockInternal(bitWriter: BitWriter, nChannels: number, sampleRateInHz: number, totalSamples: number) {

  // METADATA_BLOCK_STREAMINFO
  const blockSize = sampleRateInHz;
  // Minimum block size
  bitWriter.writeBits(blockSize, 16);
  // Maximum block size
  bitWriter.writeBits(blockSize, 16);

  const frameSize = nChannels * sampleRateInHz * 2;
  // Minimum frame size
  bitWriter.writeBits(0, 24);
  // Maximum frame size
  bitWriter.writeBits(frameSize, 24);

  // Sample rate in Hz
  bitWriter.writeBits(sampleRateInHz, 20);

  // (number of channels)-1
  bitWriter.writeBits(nChannels - 1, 3);

  // (bits per sample)-1
  bitWriter.writeBits(16 - 1, 5);

  // 36 bits
  // Total samples in stream. 'Samples' means inter-channel sample, i.e. one second
  // of 44.1Khz audio will have 44100 samples regardless of the number of channels.
  // A value of zero here means the number of total samples is unknown.
  const x = Math.floor(totalSamples / (1 << 32));
  bitWriter.writeBits(x, 4);
  bitWriter.writeBits(totalSamples & 0xFFFFFFFF, 32);

  // 128 bits
  // MD5 signature of the unencoded audio data. This allows the decoder to determine if an
  // error exists in the audio data even when the error does not result in an invalid bitstream.
  // 0 indicates that the MD5 sum is not known
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(0, 32);
}


export function writeFrame(
  bufferWrapper: FlacCompressionBufferWrapper,
  frameNumber: number,
  pcm_s16le_bytes: Uint8Array,
  nChannels: number,
  sampleRateInHz: number,
  compressed: boolean,
): Uint8Array {
  // Our compression is tailored for 16 bits stereo frames of 1024 samples.
  // If the data to encode does not fit these constraints, we will encode
  // the data without compressing it
  if (compressed && nChannels === 2 && pcm_s16le_bytes.length === 4096) {
    try {
      return writeCompressedStereoFrame(bufferWrapper, frameNumber, pcm_s16le_bytes, sampleRateInHz);
    } catch (err) {
      // Since writing an uncompressed frame is bullet proof, let's use it as a fallback to
      // avoid breaking encoding in case anything goes wrong in the compression
      console.error(`Unexpected error while trying to compress Flac frame: ${exceptionToString(err)}. Will fall back to uncompressed`);
      return writeUncompressedFrame(frameNumber, pcm_s16le_bytes, nChannels, sampleRateInHz);
    }
  } else {
    return writeUncompressedFrame(frameNumber, pcm_s16le_bytes, nChannels, sampleRateInHz);
  }
}



function writeFrameHeader(
  bitWriter: BitWriter,
  frameNumber: number,
  blockSize: number,
  compressed: boolean,
  nChannels: number,
  sampleRateInHz: number,
) {
  // FRAME_HEADER

  // sync code
  bitWriter.writeBits(0b11111111111110, 14);

  // mandatory value
  bitWriter.writeBits(0, 1);

  // Blocking strategy: fixed-blocksize stream; frame header encodes the frame number
  bitWriter.writeBits(0, 1);

  // Block size in inter-channel samples
  // 0111 : get 16 bit (blocksize-1) from end of header
  const bs = compressed ? 10 : 0b0111;
  bitWriter.writeBits(bs, 4);

  // Sample rate
  let sr = 0b0000; // get from STREAMINFO metadata block
  switch (sampleRateInHz) {
    case  88200: sr = 0b0001; break;
    case 176400: sr = 0b0010; break;
    case 192000: sr = 0b0011; break;
    case   8000: sr = 0b0100; break;
    case  16000: sr = 0b0101; break;
    case  22050: sr = 0b0110; break;
    case  24000: sr = 0b0111; break;
    case  32000: sr = 0b1000; break;
    case  44100: sr = 0b1001; break;
    case  48000: sr = 0b1010; break;
    case  96000: sr = 0b1011; break;
  }
  bitWriter.writeBits(sr, 4);

  // Channel assignment
  // 0000-0111 (number of independent channels)-1
  // or
  // 1010 if compressed stereo where we use mid/side stereo since we apply decorrelation
  bitWriter.writeBits(compressed ? 0b1010  : nChannels - 1, 4);

  // Sample size in bit
  // 100 : 16 bits
  bitWriter.writeBits(0b100, 3);

  // Reserved: mandatory value
  bitWriter.writeBits(0, 1);

  // Since we are not using variable size, we encode the frame number
  // <8-48>:"UTF-8" coded frame number (decoded number is 31 bits)
  bitWriter.writeUTF8(frameNumber);

  // Now we have 16 bits encoding block size bits - 1
  if (!compressed) {
    bitWriter.writeBits(blockSize - 1, 16);
  }

  const crc8 = get_crc_8bits(bitWriter.bytes);
  bitWriter.writeBits(crc8, 8);

}


function writeUncompressedFrameHeader(
  bitWriter: BitWriter,
  frameNumber: number,
  bytes: Uint8Array,
  nChannels: number,
  sampleRateInHz: number,
) {
  const blockSize = bytes.byteLength / (2 * nChannels);
  writeFrameHeader(bitWriter, frameNumber, blockSize, false, nChannels, sampleRateInHz);
}


function writeFrameFooter(bitWriter: BitWriter) {
  // Zero-padding to byte alignment
  bitWriter.zeroPadLastByteIfNeeded();

  // FRAME_FOOTER
  const crc16 = get_crc_16bits(bitWriter.bytes);
  bitWriter.writeBits(crc16, 16);
}


function writeUncompressedFrame(frameNumber: number, bytes: Uint8Array, nChannels: number, sampleRateInHz: number): Uint8Array {
  const bitWriter = new BitWriter();

  writeUncompressedFrameHeader(bitWriter, frameNumber, bytes, nChannels, sampleRateInHz);

  // SUBFRAMES, 1 per channel
  for (let i = 0; i < nChannels; i++) {
    writeVerbatimSubframe(bitWriter, i, nChannels, bytes);
  }

  writeFrameFooter(bitWriter);

  return bitWriter.getBuffer();
}


function writeVerbatimSubframe(bitWriter: BitWriter, channel: number, nChannels: number, bytes: Uint8Array) {
  // SUBFRAME_HEADER

  // Zero bit padding, to prevent sync-fooling string of 1s
  bitWriter.writeBits(0, 1);

  // Subframe type: SUBFRAME_VERBATIM
  bitWriter.writeBits(0b000001, 6);

  // Wasted bits-per-sample: no wasted bits-per-sample in source subblock, k=0
  bitWriter.writeBits(0, 1);

  // SUBFRAME_VERBATIM
  const nSamples = bytes.length / (nChannels * 2);

  let n = 0;
  for (let sample = 0; sample < nSamples; sample++) {
    const offset = sample * (2 * nChannels) + (channel * 2);
    bitWriter.writeBits(bytes[offset + 1]!, 8);
    bitWriter.writeBits(bytes[offset]!, 8);
    n += 2;
  }
}


function writeCompressedFrameHeader(bitWriter: BitWriter, frameNumber: number, sampleRateInHz: number) {
  writeFrameHeader(bitWriter, frameNumber, SAMPLES_PER_CHANNEL_PER_FRAME, true, 2, sampleRateInHz);
}


/**
 * This function tries to compress and encode a frame using the same compression parameters as:
 *
 * ffmpeg -y -i <input file> -ac 2 -sample_fmt s16 -compression_level 4 -frame_size 1024 -ch_mode mid_side output.flac
 *
 * If the frame cannot be compressed or would result in a frame larger than an
 * uncompressed one, the function will actually return a buffer containing an
 * uncompressed frame.
 */
function writeCompressedStereoFrame(
  bufferWrapper: FlacCompressionBufferWrapper,
  frameNumber: number,
  bytes: Uint8Array,
  sampleRateInHz: number,
): Uint8Array {
  const bitWriter = new BitWriter();

  const { left, right } = copySamples(bytes);
  decorrelateChannels(left, right);

  let leftBits = 16;
  let rightBits = 17;

  const leftWastedBits = remove_wasted_bits(left);
  leftBits -= leftWastedBits;
  // This may be a bug in ffmpeg that it returns early from remove_wasted_bits()
  // if the wasted bit count is 0, which means that the wasted bits are never evaluated
  // for the for the second channel if the first channel has no wasted bit
  const rightWastedBits = leftWastedBits === 0 ? 0 : remove_wasted_bits(right);
  rightBits -= rightWastedBits;


  writeCompressedFrameHeader(bitWriter, frameNumber, sampleRateInHz);

  // Let's encode the subframes
  if (!encodeSubframe(bufferWrapper, bitWriter, leftBits, leftWastedBits, left)) {
    return writeUncompressedFrame(frameNumber, bytes, 2, sampleRateInHz);
  }

  if (!encodeSubframe(bufferWrapper, bitWriter, rightBits, rightWastedBits, right)) {
    return writeUncompressedFrame(frameNumber, bytes, 2, sampleRateInHz);
  }

  writeFrameFooter(bitWriter);

  const totalFrameSize = bitWriter.bytes.length;

  // If we could not compress better than verbatim, let's save as verbatim
  if (totalFrameSize > MAX_FRAME_SIZE) {
    return writeUncompressedFrame(frameNumber, bytes, 2, sampleRateInHz);
  }

  return bitWriter.getBuffer();
}


/**
 * Returns true if all values are the same.
 */
function hasConstantValue(samples: Int32Array): boolean {
  const n = samples[0];
  const len = samples.length;
  for (let i = 1; i < len; i++) {
    if (samples[i] !== n) {
      return false;
    }
  }
  return true;
}


function encodeWastedBitsPerSamples(bitWriter: BitWriter, nWastedBits: number): void {
  if (nWastedBits === 0) {
    // no wasted bits-per-sample in source subblock, k=0
    bitWriter.writeBits(0, 1);
    return;
  }

  // k wasted bits-per-sample in source subblock, k-1 follows, unary coded; e.g. k=3 => 001 follows, k=7 => 0000001 follows.
  bitWriter.writeBits(1, 1);
  bitWriter.writeBits(1, nWastedBits);
}


function writeConstantSubframe(bitWriter: BitWriter, nBits: number, nWastedBits: number, value: number): void {
  // SUBFRAME_HEADER

  // Zero bit padding, to prevent sync-fooling string of 1s
  bitWriter.writeBits(0, 1);

  // Subframe type: SUBFRAME_CONSTANT
  bitWriter.writeBits(0b000000, 6);

  // Wasted bits-per-sample: no wasted bits-per-sample in source subblock, k=0
  encodeWastedBitsPerSamples(bitWriter, nWastedBits);

  // The value, encoded with the frame's number of bits per samples which is always 16
  bitWriter.writeSignedBits(value, nBits);
}


/**
 * Returns true if the subframe could be compressed, false otherwise.
 */
function encodeSubframe(
  bufferWrapper: FlacCompressionBufferWrapper,
  bitWriter: BitWriter,
  /**
   * The number of bits needed to represent the samples
   */
  nBits: number,
  /**
   * The number of wasted bits per sample
   */
  nWastedBits: number,
  /**
   * The sample buffer to encode
   */
  samples: Int32Array,
): boolean {
  if (hasConstantValue(samples)) {
    writeConstantSubframe(bitWriter, nBits, nWastedBits, samples[0]!);
    return true;
  }

  if (bufferWrapper.buffers === undefined) {
    bufferWrapper.buffers = makeFlacCompressionBuffers();
  }

  const { windowedSamples, autocorrelation, lpc, ref, coeffs, residual } = bufferWrapper.buffers;
  applyWelchWindow(samples, windowedSamples);

  computeAutoCorrelation(windowedSamples, autocorrelation);

  computeLinearPredictiveCodingCoefficients(autocorrelation, MAX_LPC_ORDER, 32, false, true, lpc);

  for (let i = 0; i < MAX_LPC_ORDER; i++) {
    ref[i] = Math.abs(lpc[i * 32 + i]!);
  }

  const bestOrder = estimateBestOrder(ref);

  // Now we know we have an LPC subframe that can be encoded, so let's write the header

  // SUBFRAME_HEADER
  // Zero bit padding, to prevent sync-fooling string of 1s
  bitWriter.writeBits(0, 1);

  // Subframe type: SUBFRAME_LPC = 1xxxxx when xxxxx is (order - 1)
  bitWriter.writeBits(1, 1);
  bitWriter.writeBits(bestOrder - 1, 5);

  // Wasted bits-per-sample: no wasted bits-per-sample in source subblock, k=0
  encodeWastedBitsPerSamples(bitWriter, nWastedBits);

  const shift = quantizeLpcCoefficients(lpc, coeffs, (bestOrder - 1) * 32, bestOrder);

  if (!encodeChooseDatapath(nBits, residual, samples, bestOrder, coeffs, shift)) {
    return false;
  }

  // We can write the warm-up samples
  for (let i = 0; i < bestOrder; i++) {
    bitWriter.writeSignedBits(residual[i]!, nBits);
  }

  // And the coefficients with a 15 bits precision
  const lpc_coeff_precision = 15;
  bitWriter.writeBits(lpc_coeff_precision - 1, 4);

  bitWriter.writeSignedBits(shift, 5);

  for (let i = 0; i < bestOrder; i++) {
    bitWriter.writeSignedBits(coeffs[i]!, lpc_coeff_precision);
  }

  calculateRiceParameters(bitWriter, residual, bestOrder);

  return true;
}


/**
 * Port of ffmpeg's calc_rice_params for the following parameter values:
 *   min_partition_order=0
 *   max_partition_order=3
 *   coding_mode=RICE
 *   lpc_coeff_precision=15
 *   exact_rice_parameters=0
 */
function calculateRiceParameters(
  bitWriter: BitWriter,
  data: Int32Array,
  order: number,
): void {
  const n = 1024;
  const udata = new Uint32Array(1024);

  const sums = new BigUint64Array(MAX_PARTITIONS);

  for (let i = order; i < n; i++) {
    udata[i] = ((data[i]!) << 1) ^ (data[i]! >> 31);
  }

  calculateSumTop(udata, sums, order);

  const params = new Int32Array(MAX_PARTITIONS);
  let bestParams = new Int32Array(MAX_PARTITIONS);
  let bestBitCount = BigInt(4294967295);
  let porder = 3;

  for (let i = 3; ; ) {
    const nBits = calculateOptimalRiceParameters(i, sums, order, params);
    if (nBits < bestBitCount) {
      porder = i;
      bestBitCount = nBits;
      bestParams.set(params, 0);
    }
    if (i === 0) {
      break;
    }

    calculateSumNext(--i, sums);
  }

  // Now let's save the subframe's Rice partitions

  // Residual coding method
  // 00 : partitioned Rice coding with 4-bit Rice parameter; RESIDUAL_CODING_METHOD_PARTITIONED_RICE follows
  bitWriter.writeBits(0, 2);

  // Partition order
  bitWriter.writeBits(porder, 4);

  const nPartitions = 1 << porder;
  const psize = 1024 >> porder;
  let res_offset = order;
  let part_end = psize;

  for (let p = 0; p < nPartitions; p++) {
    const k = bestParams[p]!;
    bitWriter.writeBits(k, 4);
    while (res_offset < part_end) {
      const value = data[res_offset++]!;
      writeGolombEncodedValue(bitWriter, value, k);
    }
    part_end = Math.min(1024, part_end + psize);
  }
}


/**
 * Port of ffmpeg's set_sr_golomb_flac
 */
function writeGolombEncodedValue(bitWriter: BitWriter, i: number, k: number) {
  const v = ((i >>> 0) << 1) ^ rightShift(i, 31);
  let e = rightShift(v, k) + 1;

  while (e > 31) {
    bitWriter.writeBits(0, 31);
    e -= 31;
  }

  bitWriter.writeBits(1, e);
  if (k) {
    const mask = rightShift(4294967295, (32 - k));
    bitWriter.writeBits(v & mask, k);
  }
}


/**
 * Port of ffmpeg's calc_sum_next for kmax=0
 */
function calculateSumNext(level: number, sums: BigUint64Array) {
  const parts = (1 << level);
  for (let i = 0; i < parts; i++) {
    sums[i] = sums[2 * i]! + sums[2 * i + 1]!;
  }
}


/**
 * Port of ffmpeg's calc_optimal_rice_params with:
 * kmax = 14
 * n = 1024
 *
 * Returns the number of bits taken by the calculated parameters.
 */
function calculateOptimalRiceParameters(
  porder: number,
  sums: BigUint64Array,
  pred_order: number,
  params: Int32Array,
): bigint {
  const max_param = 14;
  const n = 1024;

  const part = (1 << porder);
  let all_bits = BigInt(4 * part);

  let cnt = (n >> porder) - pred_order;

  for (let i = 0; i < part; i++) {
    const k = findOptimalParameter(sums[i]!, cnt, max_param);
    all_bits += riceEncodeCount(sums[i]!, cnt, BigInt(k));
    params[i] = k;
    cnt = n >> porder;
  }

  return all_bits;
}


/**
 * Port of rice_encode_count
 */
function riceEncodeCount(sum: bigint, n: number, k: bigint): bigint {
  return (BigInt(n)*(k + BigInt(1)))+((sum-BigInt(BigInt(n>>1))>>(k)));
}


/**
 * Port of find_optimal_param
 */
function findOptimalParameter(sum: bigint, n: number, max_param: number): number {
  const tmp = BigInt(n >> 1);

  if (sum <= tmp) {
    return 0;
  }
  const sum2 = (sum - tmp);
  const tmp2 = Math.floor(Number(sum2 / BigInt(n)));
  const clipped = clipInt32(tmp2);
  const k = Math.floor(Math.log2(clipped > 0 ? clipped : 1));
  return Math.min(k, max_param);
}


/**
 * Port of ffmpeg's calc_sum_top for:
 *
 * pmax = 3
 * kmax = 0
 * n = 1024
 */
function calculateSumTop(
  data: Uint32Array,
  sums: BigUint64Array,
  order: number,
): void {
  const pmax = 3;
  const n = 1024;
  const parts = MAX_PARTITIONS;

  let res_offset = order;
  let res_end_offset = (n >> pmax);

  for (let i = 0; i < parts; i++) {
    let sum = BigInt(0);
    while (res_offset < res_end_offset) {
      sum += BigInt(data[res_offset++]!);
    }
    sums[i] = sum;
    res_end_offset += (n >> pmax);
  }
}


/**
 * Port of ffmpeg's lpc_encode_choose_datapath for a fixed coefficient precision of 15
 *
 * Returns true if an encoding was found; false otherwise
 */
function encodeChooseDatapath(
  bitsPerSample: number,
  residual: Int32Array,
  samples: Int32Array,
  order: number,
  coeffs: Int32Array,
  shift: number,
): boolean {
  let max_residual_value = 0;
  const max_sample_value = (1 << (bitsPerSample - 1));
  /* This calculates the max size of any residual with the current
   * predictor, so we know whether we need to check the residual */
  for (let i = 0; i < order; i++) {
    max_residual_value += Math.abs(max_sample_value * coeffs[i]!);
  }
  max_residual_value = rightShift(max_residual_value, shift);
  max_residual_value += max_sample_value;

  if (max_residual_value > 2147483647) {
    if (!encodeWithResidualLimit(residual, samples, order, coeffs, shift)) {
      return false;
    }
  } else if (bitsPerSample + 15 + Math.log2(order) <= 32) {
    lcpEncode(residual, samples, order, coeffs, shift, false);
  } else {
    lcpEncode(residual, samples, order, coeffs, shift, true);
  }

  return true;
}


function clipInt32(n: number | bigint): number {
  if (n < -2147483648) return -2147483648;
  if (n > 2147483647) return 2147483647;
  return Number(n);
}


function lcpEncode(
  residual: Int32Array,
  samples: Int32Array,
  order: number,
  coeffs: Int32Array,
  shift: number,
  use32Bits: boolean,
): void {
  const len = 1024;
  for (let i = 0; i < order; i++) {
    residual[i] = samples[i]!;
  }

  for (let i = order; i < len; i++) {
    let p = 0;
    for (let j = 0; j < order; j++) {
      const c = coeffs[j]!;
      const s = samples[(i - 1) - j]!;
      p += c * s;
    }
    let delta = rightShift(p, shift);
    if (use32Bits) {
      delta = clipInt32(delta);
    }
    residual[i] = samples[i]! - delta;
  }
}


/**
 * Tries to encode. Returns true on success or false if the data
 * cannot be encoded without hitting some integer overflow.
 */
function encodeWithResidualLimit(
  residual: Int32Array,
  samples: Int32Array,
  order: number,
  coeffs: Int32Array,
  shift: number,
): boolean {
  const len = 1024;
  for (let i = 0; i < order; i++) {
    residual[i] = samples[i]!;
  }

  for (let i = order; i < len; i++) {
    let p = 0;
    let tmp;
    for (let j = 0; j < order; j++) {
      p += coeffs[j]! * samples[(i - 1) - j]!;
    }
    p = rightShift(p, shift);
    tmp = samples[i]! - p;
    if (tmp <= -2147483648 || tmp > 2147483647) {
      return false;
    }
    residual[i] = tmp;
  }
  return true;
}


/**
 * The >>= operator does not work as we need for n larger than 32 bits,
 * so we calculate the shift here.
 *
 * For instance: (4584833024 >>= 11) gives 141536 but we expect 2238688
 */
function rightShift(n: number, shift: number): number {
  return Math.floor(n * Math.pow(2, -shift));
}


/**
 * Port of ffmpeg's quantize_lpc_coefs for the fixed parameters:
 *
 *   precision=15 min_shift=0 max_shift=15 zero_shift=0
 *
 * Returns the shift value.
 *
 * Warning: expects lpcOut to be initialized with zeroes
 */
function quantizeLpcCoefficients(lpcIn: Float64Array, lpcOut: Int32Array, lpc_offset: number, order: number): number {
  const precision = 15;
  const min_shift = 0;
  const max_shift = 15;

  const qmax = (1 << (precision - 1)) - 1;

  let cmax = 0.0;
  for (let i = 0; i < order; i++) {
    cmax = Math.max(cmax, Math.abs(lpcIn[lpc_offset + i]!));
  }

  if(cmax * (1 << max_shift) < 1.0) {
    // Nothing to do since lpcOut
    return 0;
  }

  let shift = max_shift;
  while ((cmax * (1 << shift) > qmax) && (shift > min_shift)) {
    shift--;
  }

  if (shift == 0 && cmax > qmax) {
    const scale = qmax / cmax;
    for (let i = 0; i < order; i++) {
      lpcIn[lpc_offset + i]! *= scale;
    }
  }

  let error = 0;
  for (let i = 0; i < order; i++) {
    error -= lpcIn[lpc_offset + i]! * (1 << shift);
    let e = Math.round(error);
    if (e < -qmax) {
      e = -qmax;
    } else if (e > qmax) {
      e = qmax;
    }
    lpcOut[i] = e;
    error -= lpcOut[i]!;
  }

  return shift;
}


/**
 * Port of ffmpeg's estimate_best_order for a fix min_order of 1
 * and a fixed max_order of 8.
 */
function estimateBestOrder(ref: Float64Array): number {
  let estimate = 1;
  for(let i = MAX_LPC_ORDER - 1; i >= 0; i--) {
    if (ref[i]! > 0.10) {
      estimate = i + 1;
      break;
    }
  }
  return estimate;
}


/**
 * Port of ffmpeg's lpc_compute_autocorr_c for a fixed block size of 1024
 * and a lag of 8
 */
function computeAutoCorrelation(windowedSamples: Float64Array, autocorrelation: Float64Array) {
  const lag = 8;
  const len = 1024;
  for (let j = 0; j < lag; j += 2) {
    let sum0 = 1.0;
    let sum1 = 1.0;
    for (let i = j; i < len; i++) {
      sum0 += windowedSamples[i]! * windowedSamples[i - j]!;
      if (i - j - 1 > 0) {
        sum1 += windowedSamples[i]! * windowedSamples[i - j - 1]!;
      }
    }
    autocorrelation[j] = sum0;
    autocorrelation[j + 1] = sum1;
  }

  let sum = 1.0;
  for (let i = lag - 1; i < len; i += 2) {
    if (i - lag > 0) {
      sum += windowedSamples[i]! * windowedSamples[i - lag]!
      + windowedSamples[i + 1]! * windowedSamples[i - lag + 1]!;
    }
  }
  autocorrelation[lag] = sum;
}


/**
 * Port of ffmpeg's lpc_apply_welch_window_c for a fixed block size of 1024
 */
function applyWelchWindow(samples: Int32Array, windowedSamples: Float64Array): void {
  const len = 1024;
  const n2 = len / 2;
  const c = 2.0 / (len - 1.0);

  const offset = n2;
  for (let i = 0; i < n2; i++) {
    let w = c - n2 + i;
    w = 1.0 - (w * w);
    windowedSamples[offset - i - 1] = samples[offset - i - 1]! * w;
    windowedSamples[offset + i] = samples[offset + i]! * w;
  }
}


/**
 * If all the sample values have common zeroes at the beginning, we can shift them.
 * For instance, if we have 3 samples represented like this in binary:
 *
 *   1000010011011000
 *   0001111010000000
 *   1010001100001100
 *
 * they all end with 00, so we can shift the values by 2 bits and report that
 * we have save 2 "wasted" bits.
 */
function remove_wasted_bits(samples: Int32Array): number {
  let tmp = 0;
  for (let i = 0; i < samples.length; i++) {
    tmp |= samples[i]!;
    if (tmp & 1) {
      // If any value uses the first bit, there is no bit we can save
      return 0;
    }
  }

  if (tmp === 0) {
    // If all the values are zeroes, we will be able to compress
    // extremely well so we don't need to deal with wasted bits
    return 0;
  }

  const wasted = countTrailingZeroBits(tmp);
  for (let i = 0; i < samples.length; i++) {
    samples[i]! >>= wasted;
  }
  return wasted;
}


function countTrailingZeroBits(value: number): number {
  invariant(value !== 0, `Should not invoke countTrailingZeroBits on ${value}`);
  let n = 0;
  while ((value & 1) === 0) {
    n++;
    value >>= 1;
  }
  return n;
}


/**
 * Stereo channels are likely to have similar information on the left and right channels.
 * Decorrelation helps taking advantage of that to compress better by encoding the sums
 * and differences instead of the raw samples. The idea is that the differences are likely
 * to be small when the channels are similar, which means that the stream representing the
 * differences should compress well.
 *
 * Of course, since we use Flac as a way to downmix to stereo any number of channels,
 * this assumption may not always be correct, but for the sake of simplicity, we
 * always do this for now.
 *
 * Note that after this operation, the values in the right array need 17 bits to
 * be represented while the left array values will still be representable with just
 * 16 bits.
 */
function decorrelateChannels(left: Int32Array, right: Int32Array): void {
  const n = left.length;
  for (let i = 0; i < n; i++) {
    const tmp = left[i]!;
    left[i]  = (tmp + right[i]!) >> 1;
    right[i] =  tmp - right[i]!;
  }
}


/**
 * Given an interleaved stereo frame, returns the data split in 2 separate arrays.
 * We use Int32Array instead of Int16Array because the operations that will happen
 * after this step will need more than 16 bits.
 */
function copySamples(bytes: Uint8Array): { left: Int32Array, right: Int32Array } {
  const nSamples = bytes.byteLength / 4;
  const samples = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const left = new Int32Array(nSamples);
  const right = new Int32Array(nSamples);


  for (let i = 0, j = 0; i < nSamples; i++, j+=4) {
    left[i] = samples.getInt16(j, true);
    right[i] = samples.getInt16(j + 2, true);
  }

  return {
    left,
    right,
  };
}
